import type {
  ActivityStatus,
  EventStatus,
  MonthlyFeeSavePayload,
  SingleContact,
} from "gsintegrations";
import { axiosClient } from "../api/axiosClient";
import type { ActivityType } from "../types/activity.types";
import type {
  Activity,
  UpdateParticipationRequest,
} from "../types/activity.types";
import { transformActivityDates } from "../utils/dateTransformers";
import type { InventoryListQueryFilters } from "../hooks/useTerreiroInventoryListQuery";

export interface Terreiro {
  id: string;
  uuid: string;
  name: string;
  imageUrl?: string;
  createdByUserId: string;
  status: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  updatedByUserId?: string;
  addressId?: string;
  createdAt: string;
  updatedAt: string;
  badgeTier?: "STARTER" | "BASIC" | "ACTIVE" | "VERIFIED";
  completenessScore?: number;
  isPublicProfile?: boolean;
  publicDescription?: string | null;
  publicContactEmail?: string | null;
  religionId?: string | null;
  address?: {
    city?: string;
    state?: string;
    neighborhood?: string;
  } | null;
  religion?: {
    id: string;
    name: string;
  } | null;
}

export type TerreiroUpdatePayload = Partial<
  Omit<Terreiro, "uuid"> & {
    documentType: "CPF" | "CNPJ" | null;
    document: string | null;
    foundationDate: string;
    biography: string | null;
    isPublicProfile: boolean;
    publicDescription: string | null;
    publicContactEmail: string | null;
  }
>;

export interface PublicTerreiroSearchParams {
  search?: string;
  city?: string;
  state?: string;
  name?: string;
  religionId?: string;
  limit?: number;
  offset?: number;
}

export interface RequestJoinTerreiroPayload {
  terreiroId: string;
  message?: string;
  acceptedPrivacyPolicy?: boolean;
  acknowledgedFee?: boolean;
  feeAmountSnapshot?: number | null;
  feeDueDaySnapshot?: number | null;
}

export interface JoinRequestHistoryItem {
  id: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  message: string;
  createdAt: string;
  reviewDate?: string;
  reviewNotes?: string;
  consentSummary?: {
    acceptedPrivacyPolicy?: boolean;
    acknowledgedFee?: boolean;
    feeAmountSnapshot?: number | null;
    feeDueDaySnapshot?: number | null;
    policySource?: "SYSTEM" | "CUSTOM";
    policyKey?: string;
    policyVersion?: number;
    policyTitle?: string;
  };
}

export interface PublicTerreiroApprover {
  mediumId: string;
  userId?: string | null;
  name: string;
  imageUrl?: string | null;
  roles: string[];
}

export interface PublicTerreiroContact {
  type?: string;
  value?: string;
  name?: string;
}

export interface PublicTerreiroDetail extends Terreiro {
  biography?: string | null;
  privacyPolicyUrl?: string | null;
  policyContent?: {
    source: "SYSTEM" | "CUSTOM";
    key: string;
    title: string;
    markdown: string;
    version?: number;
    updatedAt?: string;
  } | null;
  contacts?: PublicTerreiroContact[];
  monthlyFee?: {
    amount: number;
    dueDay: number;
    paymentMethod?: string | null;
  } | null;
  approvers?: PublicTerreiroApprover[];
}

export interface TerreiroPolicyContent {
  source: "SYSTEM" | "CUSTOM";
  key: string;
  title: string;
  markdown: string;
  version?: number;
  updatedAt?: string;
}

export interface FinancialReportCategoryAggregation {
  categoryId: string | null;
  categoryName: string;
  total: number;
  count: number;
}

export interface FinancialReportResponse {
  terreiroId: string;
  month: number;
  year: number;
  period: {
    start: string;
    end: string;
  };
  summary: {
    totalIncome: number;
    totalOutcome: number;
    netFlow: number;
    incomeCount: number;
    outcomeCount: number;
    projectedSettlementCount: number;
    consolidatedSettlementCount: number;
    correctionCount: number;
  };
  formatted: {
    totalIncome: string;
    totalOutcome: string;
    netFlow: string;
  };
  incomeByCategory: FinancialReportCategoryAggregation[];
  outcomeByCategory: FinancialReportCategoryAggregation[];
}

export interface FinancialSetupStatusResponse {
  terreiroId: string;
  checks: {
    hasMonthlyFee: boolean;
    hasBankAccount: boolean;
    hasIncomeCategories: boolean;
    hasOutcomeCategories: boolean;
  };
  counts: {
    bankAccounts: number;
    incomeCategories: number;
    outcomeCategories: number;
  };
  isReady: boolean;
  nextSteps: string[];
}

export type AttendanceInsightsPeriod = "WEEKLY" | "MONTHLY" | "YEARLY";

export interface AttendanceInsightsMedium {
  mediumId: string;
  mediumName: string;
  mediumAvatarUrl?: string | null;
  invites: number;
  confirmed: number;
  present: number;
  absent: number;
  declined: number;
  confirmationRate: number;
  presenceRate: number;
  absenceGap: number;
}

export interface AttendanceInsightsResponse {
  terreiroId: string;
  period: {
    month: number;
    year: number;
    start: string;
    end: string;
  };
  summary: {
    invites: number;
    confirmed: number;
    present: number;
    absent: number;
    declined: number;
    confirmationRate: number;
    presenceRate: number;
    absenceGap: number;
  };
  /**
   * How many mediums belong in this insights build, before any server cap.
   * The attendance list groups only when this equals `mediums.length`.
   */
  mediumTotal?: number;
  /**
   * True only when `mediums` is the full set, zeros included.
   * Today's API omits it and returns at most 5 mediums.
   */
  mediumsComplete?: boolean;
  mediums: AttendanceInsightsMedium[];
}

export type TerreiroMediunsOptions = {
  includeSelf?: boolean;
};

export const terreiroRepository = {
  normalizePublicTerreiroContacts(
    rawContacts: unknown,
  ): PublicTerreiroContact[] {
    if (!Array.isArray(rawContacts)) {
      return [];
    }

    const normalized: PublicTerreiroContact[] = [];

    for (const entry of rawContacts) {
      if (!entry || typeof entry !== "object") {
        continue;
      }

      const contact = entry as Record<string, unknown>;
      const valueFromNewContract =
        typeof contact.value === "string" ? contact.value : undefined;
      const valueFromLegacyContract =
        typeof contact.phone === "string" ? contact.phone : undefined;
      const nameFromNewContract =
        typeof contact.name === "string" ? contact.name : undefined;
      const nameFromLegacyContract =
        typeof contact.contactPerson === "string"
          ? contact.contactPerson
          : undefined;

      const value = valueFromNewContract ?? valueFromLegacyContract;
      const name = nameFromNewContract ?? nameFromLegacyContract;

      if (!value && !name) {
        continue;
      }

      normalized.push({
        type:
          typeof contact.type === "string" && contact.type.trim().length > 0
            ? contact.type
            : "Telefone",
        value,
        name,
      });

      if (normalized.length >= 3) {
        break;
      }
    }

    return normalized;
  },

  async createTerreiro(data: Partial<Terreiro>) {
    const response = await axiosClient.post(`/terreiro`, data, {
      withCredentials: true,
    });
    return response.data;
  },
  async getAllTerreiros() {
    const response = await axiosClient.get(`/terreiro`, {
      withCredentials: true,
    });
    return response.data;
  },
  async getTerreiroById(id: string) {
    const response = await axiosClient.get(`terreiro/${id}`, {
      withCredentials: true,
    });
    return response.data;
  },
  async updateTerreiro(id: string, data: TerreiroUpdatePayload) {
    // Convert empty strings to null for nullable constrained fields so the API
    // schema (minLength: 1 / format: email) is never violated when clearing them.
    const sanitized: TerreiroUpdatePayload = {
      ...data,
      ...(data.document !== undefined
        ? { document: data.document || null }
        : {}),
      ...(data.publicContactEmail !== undefined
        ? { publicContactEmail: data.publicContactEmail || null }
        : {}),
    };
    const response = await axiosClient.put(`terreiro/${id}`, sanitized, {
      withCredentials: true,
    });
    return response.data;
  },
  async updateMonthlyFee(
    terreiroId: string,
    monthlyFeeId: string,
    payload: MonthlyFeeSavePayload,
  ) {
    const { data } = await axiosClient.put(
      `terreiro/${terreiroId}/monthly-fees/${monthlyFeeId}`,
      payload,
      { withCredentials: true },
    );
    return data;
  },
  async updateTerreiroAvatar(
    terreiroId: string,
    file: File | Blob,
    filename?: string,
  ): Promise<Terreiro> {
    const form = new FormData();
    form.append(
      "avatar",
      file as Blob,
      filename ?? (file instanceof File ? file.name : "avatar"),
    );
    const response = await axiosClient.patch<Terreiro>(
      `/terreiro/${terreiroId}/avatar/upload`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      },
    );
    return response.data;
  },
  async archiveTerreiro(id: string) {
    const response = await axiosClient.post(
      `terreiro/${id}/archive`,
      {},
      { withCredentials: true },
    );
    return response.data;
  },
  async restoreTerreiro(id: string) {
    const response = await axiosClient.post(
      `terreiro/${id}/restore`,
      {},
      { withCredentials: true },
    );
    return response.data;
  },
  async deleteTerreiro(id: string) {
    const response = await axiosClient.delete(`terreiro/${id}`, {
      withCredentials: true,
    });
    return response.data;
  },
  async getFirstActiveTerreiro(terreiroId: string) {
    const response = await axiosClient.get(`terreiro/${terreiroId}/active`, {
      withCredentials: true,
    });
    return response.data as Terreiro;
  },
  async getTerreiroContacts(terreiroId: string) {
    const response = await axiosClient.get(`contact/terreiro/${terreiroId}`, {
      withCredentials: true,
    });
    return response.data as SingleContact[];
  },
  async updateContact(id: string, data: Partial<SingleContact>) {
    const response = await axiosClient.put(`contact/${id}`, data, {
      withCredentials: true,
    });
    return response.data;
  },
  async createContact(data: SingleContact) {
    const response = await axiosClient.post(`contact`, data, {
      withCredentials: true,
    });
    return response.data;
  },
  async deleteContact(id: string) {
    const response = await axiosClient.delete(`contact/${id}`, {
      withCredentials: true,
    });
    return response.data;
  },
  async getTerreiroAddress(id: string) {
    const response = await axiosClient.get(`address/terreiro/${id}`, {
      withCredentials: true,
    });
    return response.data;
  },
  async getTerreiroMediuns(
    terreiroId: string,
    options?: TerreiroMediunsOptions,
  ) {
    const response = await axiosClient.get(`/terreiro/${terreiroId}/mediuns`, {
      withCredentials: true,
      params: {
        includeSelf: options?.includeSelf,
      },
    });
    return response.data;
  },
  async getMediumProfileView(terreiroId: string, mediumId: string) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/medium/${mediumId}/profile-view`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  async getTerreiroActivities(
    terreiroId: string,
    status: ActivityStatus = "MODEL",
    date?: string,
    includeCounts: boolean = true,
    type?: ActivityType,
    period?: { month: number; year: number },
  ) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/activities`,
      {
        withCredentials: true,
        params: {
          // include status only when it's provided (defaults to MODEL)
          status: status ?? undefined,
          // include date when provided (ISO yyyy-mm-dd)
          date: date ?? undefined,
          // include status counts
          includeCounts,
          type: type ?? undefined,
          month: period?.month,
          year: period?.year,
        },
      },
    );
    return response.data;
  },
  async getActivitiesByMedium(
    mediumId: string,
    status?: ActivityStatus,
    date?: string,
    type?: ActivityType,
  ) {
    const response = await axiosClient.get(`/activity/medium/${mediumId}`, {
      withCredentials: true,
      params: {
        status: status ?? undefined,
        date: date ?? undefined,
        type: type ?? undefined,
      },
    });
    return response.data;
  },
  async getTerreiroActivity(terreiroId: string, activityId: string) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/activity/${activityId}`,
      {
        withCredentials: true,
      },
    );
    return transformActivityDates(response.data);
  },
  async getActivityByMedium(mediumId: string, activityId: string) {
    const response = await axiosClient.get(
      `/activity/medium/${mediumId}/activity/${activityId}`,
      {
        withCredentials: true,
      },
    );
    return transformActivityDates(response.data);
  },
  async getTerreiroEvents(
    terreiroId: string,
    status: EventStatus = "MODEL",
    includeCounts: boolean = true,
    period?: { month: number; year: number },
  ) {
    const response = await axiosClient.get(`/terreiro/${terreiroId}/events`, {
      withCredentials: true,
      params: {
        status,
        includeCounts,
        ...(period ? { month: period.month, year: period.year } : {}),
      },
    });
    return response.data;
  },
  async getTerreiroInventoryItems(
    terreiroId: string,
    filters?: InventoryListQueryFilters,
  ) {
    const response = await axiosClient.get(`/inventory-item`, {
      withCredentials: true,
      params: {
        terreiroId,
        ...(filters?.name ? { name: filters.name } : {}),
        ...(filters?.status ? { status: filters.status } : {}),
        sortBy: "createdAt",
        sortOrder: filters?.sortOrder ?? "desc",
      },
    });
    return response.data;
  },
  async getTerreiroBankAccounts(terreiroId: string) {
    const response = await axiosClient.get(
      `/bank-account?terreiroId=${terreiroId}`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  async getMonthlyFees(terreiroId: string) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/monthly-fees`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  // get the outcome from current month based on /:id/outcomes/current-month
  async getCurrentMonthOutcome(terreiroId: string) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/outcomes/current-month`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  // get the income from current month based on /:id/incomes/current-month
  async getCurrentMonthIncome(terreiroId: string) {
    const response = await axiosClient.get(
      `/terreiro/${terreiroId}/incomes/current-month`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
  /**
   * Get financial overview for an owner scope (terreiro or personal medium).
   * Includes totalIncome, totalOutcome, netFlow, totalBalance, corrections and categories for the current month.
   *
   * @param scope - Exactly one owner scope: terreiroId xor mediumId
   * @returns Financial overview data
   */
  async getFinancialOverviewByOwnerScope(scope: {
    terreiroId?: string;
    mediumId?: string;
    month?: number;
    year?: number;
  }) {
    const { terreiroId, mediumId, month, year } = scope;

    if ((terreiroId && mediumId) || (!terreiroId && !mediumId)) {
      throw new Error(
        "Exactly one owner scope is required: terreiroId xor mediumId",
      );
    }

    const response = await axiosClient.get(`/terreiro/financial-overview`, {
      withCredentials: true,
      params: {
        terreiroId,
        mediumId,
        month,
        year,
      },
    });
    return response.data;
  },
  async getFinancialOverview(terreiroId: string) {
    return this.getFinancialOverviewByOwnerScope({ terreiroId });
  },
  async getFinancialSetupStatus(terreiroId: string) {
    const response = await axiosClient.get<FinancialSetupStatusResponse>(
      `/terreiro/${terreiroId}/financial-setup-status`,
      {
        withCredentials: true,
      },
    );

    return response.data;
  },
  async getFinancialReport(input: {
    terreiroId: string;
    month?: number;
    year?: number;
  }) {
    const { terreiroId, month, year } = input;
    const response = await axiosClient.get<FinancialReportResponse>(
      `/terreiro/${terreiroId}/financial-report`,
      {
        withCredentials: true,
        params: {
          month,
          year,
        },
      },
    );

    return response.data;
  },
  async exportFinancialReportCsv(input: {
    terreiroId: string;
    month?: number;
    year?: number;
  }) {
    const { terreiroId, month, year } = input;
    const response = await axiosClient.get<Blob>(
      `/terreiro/${terreiroId}/financial-report/export`,
      {
        withCredentials: true,
        params: {
          month,
          year,
        },
        responseType: "blob",
      },
    );

    return response.data;
  },
  async exportActivitiesParticipationCsv(input: {
    terreiroId: string;
    month?: number;
    year?: number;
    status?: string;
    type?: ActivityType;
  }) {
    const { terreiroId, month, year, status, type } = input;
    const response = await axiosClient.get<Blob>(
      `/terreiro/${terreiroId}/activities/export`,
      {
        withCredentials: true,
        params: {
          month,
          year,
          status,
          type,
        },
        responseType: "blob",
      },
    );

    return response.data;
  },
  async exportEventsParticipationCsv(input: {
    terreiroId: string;
    month?: number;
    year?: number;
    status?: string;
  }) {
    const { terreiroId, month, year, status } = input;
    const response = await axiosClient.get<Blob>(
      `/terreiro/${terreiroId}/events/export`,
      {
        withCredentials: true,
        params: {
          month,
          year,
          status,
        },
        responseType: "blob",
      },
    );

    return response.data;
  },
  async getAttendanceInsights(input: {
    terreiroId: string;
    period?: AttendanceInsightsPeriod;
    month?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
  }) {
    const { terreiroId, period, month, year, startDate, endDate } = input;

    const response = await axiosClient.get<AttendanceInsightsResponse>(
      `/terreiro/${terreiroId}/attendance-insights`,
      {
        withCredentials: true,
        params: {
          period,
          month,
          year,
          startDate,
          endDate,
        },
      },
    );

    return response.data;
  },
  // get the categories with dynamic flow query param
  async getTerreiroCategories(_terreiroId: string, flow?: string) {
    const flowQuery = flow ? `?flow=${flow}` : "";
    const response = await axiosClient.get(`/categories${flowQuery}`, {
      withCredentials: true,
    });
    return response.data;
  },
  async getMediumById(mediumId: string) {
    const response = await axiosClient.get(`/medium/${mediumId}`, {
      withCredentials: true,
    });
    return response.data;
  },

  /**
   * Update a member's participation status for an activity.
   *
   * @param terreiroId - The terreiro ID
   * @param activityId - The activity ID
   * @param memberId - The member ID (medium)
   * @param data - The participation update payload
   * @returns The updated activity object
   */
  async updateActivityMemberParticipation(
    terreiroId: string,
    activityId: string,
    memberId: string,
    data: UpdateParticipationRequest,
  ): Promise<Activity> {
    const response = await axiosClient.put<Activity>(
      `/terreiro/${terreiroId}/activities/${activityId}/members/${memberId}/participation`,
      data,
      { withCredentials: true },
    );
    return response.data;
  },

  /**
   * Join a terreiro using an invitation code or QR code data.
   *
   * @param terreiroCode - The terreiro invitation code
   * @returns The joined terreiro object
   */
  async joinTerreiro(terreiroCode: string): Promise<Terreiro> {
    const response = await axiosClient.post<Terreiro>(
      `/terreiro/join`,
      { code: terreiroCode },
      { withCredentials: true },
    );
    return response.data;
  },

  async searchPublicTerreiros(
    params: PublicTerreiroSearchParams,
  ): Promise<Terreiro[]> {
    const response = await axiosClient.get<Terreiro[]>(
      `/terreiro/public/search`,
      {
        withCredentials: true,
        params,
      },
    );
    return response.data;
  },

  async getPublicTerreiroById(id: string): Promise<PublicTerreiroDetail> {
    const response = await axiosClient.get<PublicTerreiroDetail>(
      `/terreiro/public/${id}`,
      {
        withCredentials: true,
      },
    );

    return {
      ...response.data,
      contacts: this.normalizePublicTerreiroContacts(response.data?.contacts),
    };
  },

  async requestJoinTerreiro(payload: RequestJoinTerreiroPayload) {
    const response = await axiosClient.post(`/terreiro-join/request`, payload, {
      withCredentials: true,
    });
    return response.data;
  },

  async getJoinRequestHistory(terreiroId: string): Promise<{
    items: JoinRequestHistoryItem[];
  }> {
    const response = await axiosClient.get(`/terreiro-join/request/history`, {
      withCredentials: true,
      params: { terreiroId },
    });
    return response.data;
  },

  async getTerreiroPolicy(terreiroId: string): Promise<TerreiroPolicyContent> {
    const response = await axiosClient.get<TerreiroPolicyContent>(
      `/terreiro/${terreiroId}/policy`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  async updateTerreiroCustomPolicy(
    terreiroId: string,
    payload: { title: string; markdown: string },
  ): Promise<TerreiroPolicyContent> {
    const response = await axiosClient.put<TerreiroPolicyContent>(
      `/terreiro/${terreiroId}/policy/custom`,
      payload,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  async updateTerreiroPolicySource(
    terreiroId: string,
    source: "SYSTEM" | "CUSTOM",
  ): Promise<TerreiroPolicyContent> {
    const response = await axiosClient.put<TerreiroPolicyContent>(
      `/terreiro/${terreiroId}/policy/source`,
      { source },
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
};
